import { useEffect, useState, type MutableRefObject } from "react";
import type { PublicQuote } from "@forgepoint/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BackButton } from "@/components/BackButton";
import { ConflictDialog } from "./ConflictDialog";
import { DocumentEditorPanes } from "./DocumentEditorPanes";
import { DocumentEditorToolbar } from "./DocumentEditorToolbar";
import { DocumentPreviewScreen } from "./DocumentPreviewScreen";
import { LockedDocumentView } from "./LockedDocumentView";
import { SaveAsTemplateButton } from "./SaveAsTemplateButton";
import { SaveIndicator } from "./SaveIndicator";
import { ViewToggle } from "./ViewToggle";
import type { DocumentAssetUrls } from "./types";
import { useAutosave } from "./useAutosave";
import { useDocumentEditor } from "./useDocumentEditor";
import type { StoredQuoteDocument } from "./quoteDocumentsStore";

/** Footer shown once the quote is finalized on the server mid-edit. */
const FINALIZED_LOCK_NOTICE =
  "This quote was finalized elsewhere, so its document is now locked and read-only.";

/** Hint shown beside the back button while previewing. */
const PREVIEW_HINT = "exact customer view · Esc returns to Edit";

interface QuoteDocumentEditorProps {
  quoteId: string;
  publicQuote: PublicQuote;
  assetUrls: DocumentAssetUrls;
  seed: StoredQuoteDocument;
  onLocalAsset: (assetId: string, file: File) => void;
  discardedRef: MutableRefObject<boolean>;
  onSaved: (document: StoredQuoteDocument) => void;
  onDocumentSaved?: (html: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  onRequestClose: () => void;
}

/**
 * The mounted three-pane editor, hosted in a full-screen dialog and split out
 * so the editor hook is seeded once from a resolved document rather than
 * remounting on every fetch state change.
 * @param quoteId - The saved quote's id
 * @param publicQuote - The customer-safe quote projection
 * @param assetUrls - Resolved signed asset urls keyed by asset id, including local upload previews
 * @param seed - The stored document seeding the editor and save version
 * @param onLocalAsset - Registers an uploaded asset id and file so its preview outlives the editor
 * @param discardedRef - Set by the tab when the user discards, suppressing the unmount flush
 * @param onSaved - Called with the persisted document after each successful save
 * @param onDocumentSaved - Called after an autosave persists, with the saved HTML
 * @param onDirtyChange - Called when the unsaved-changes state flips
 * @param onRequestClose - Called when the user asks to close the editor
 */
export function QuoteDocumentEditor({
  quoteId,
  publicQuote,
  assetUrls,
  seed,
  onLocalAsset,
  discardedRef,
  onSaved,
  onDocumentSaved,
  onDirtyChange,
  onRequestClose,
}: QuoteDocumentEditorProps) {
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const editor = useDocumentEditor(
    publicQuote,
    seed.document,
    isUploadingImage,
  );
  const [isPreview, setIsPreview] = useState(false);
  const [lockAcknowledged, setLockAcknowledged] = useState(false);

  const autosave = useAutosave(
    quoteId,
    editor.documentHtml,
    seed.version,
    editor.resetDocument,
    (html, version) => {
      onSaved({ ...seed, document: html, version });
      onDocumentSaved?.(html);
    },
    discardedRef,
  );

  useEffect(() => {
    onDirtyChange(autosave.isDirty);
  }, [autosave.isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isPreview) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsPreview(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isPreview]);

  if (autosave.isLocked) {
    return (
      <>
        <AlertDialog open={!lockAcknowledged}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>This quote is finalized</AlertDialogTitle>
              <AlertDialogDescription>
                The document is locked and your changes were not saved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setLockAcknowledged(true)}>
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <LockedDocumentView
          publicQuote={publicQuote}
          assetUrls={assetUrls}
          documentHtml={editor.renderedHtml}
          notice={FINALIZED_LOCK_NOTICE}
          onClose={onRequestClose}
        />
      </>
    );
  }

  if (isPreview) {
    return (
      <DocumentPreviewScreen
        documentHtml={editor.renderedHtml}
        quote={publicQuote}
        assetUrls={assetUrls}
        hint={PREVIEW_HINT}
        onExitPreview={() => setIsPreview(false)}
        onRequestClose={onRequestClose}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <ConflictDialog
        open={autosave.conflict !== null}
        onReload={autosave.reloadServerVersion}
        onKeep={autosave.keepLocalVersion}
      />
      <header className="flex items-center gap-3 border-b bg-background px-4 py-2 shrink-0">
        <BackButton onClick={onRequestClose} />
        <DocumentEditorToolbar editor={editor} />
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <div className="flex items-center gap-1">
            <SaveAsTemplateButton
              quoteId={quoteId}
              isDirty={autosave.isDirty}
              isSaving={autosave.isSaving}
            />
          </div>
          <SaveIndicator
            isSaving={autosave.isSaving}
            isDirty={autosave.isDirty}
          />
          <ViewToggle isPreview={isPreview} onChange={setIsPreview} />
        </div>
      </header>
      {autosave.saveError && (
        <p className="border-b bg-destructive/10 px-4 py-1.5 text-xs text-destructive">
          {autosave.saveError}
        </p>
      )}
      <DocumentEditorPanes
        editor={editor}
        quote={publicQuote}
        assetUrls={assetUrls}
        onLocalAsset={onLocalAsset}
        isUploadingImage={isUploadingImage}
        onUploadingChange={setIsUploadingImage}
      />
    </div>
  );
}
